import { useMemo } from "react";
import * as Sentry from "@sentry/react";

import { useGetVersionStatusQuery } from "fond/api";
import { solverParameterToLayerId } from "fond/layers";
import { Maybe } from "fond/types";

interface UseGetPlannerLayerReturns {
  layerKeys: string[];
}

export const useGetPlannerLayers = (versionId: Maybe<string>): UseGetPlannerLayerReturns => {
  const { data: versionStatus } = useGetVersionStatusQuery(versionId ?? "", { skip: !versionId });
  const layerKeys = useMemo(
    () =>
      Object.entries(versionStatus?.InputFiles ?? {})
        .map(([solverParam, value]) => {
          // T1/T2 Hub edits live in a separate layer, but are smooshed together with the hubs output layer.
          // Because of this, we don't want to consider these as actual layers present in the project.
          if (!value || ["closure", "cabinet", "t1_hub_geometry", "t2_hub_geometry"].includes(solverParam)) return null;

          try {
            // The versionStatus.InputFiles object contains 'solver parameters' as keys; these need to be mapped
            // back to layer IDs.
            return solverParameterToLayerId(solverParam);
          } catch (e) {
            // There's a planner layer we aren't aware of; record it for future investigation.
            Sentry.captureException(e);
            return null;
          }
        })
        .filter((layerKey) => layerKey !== null),
    [versionStatus]
  );

  return { layerKeys };
};
